import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { NexusIcon } from '@nexus/react';
import './PortalDropdown.scss';
import UserIcon from '@nexus/core/dist/assets/icons/action/ic_account_circle_24px.svg';
import styles from './Header.module.scss';

export type IOption = {
    label: string;
    value?: string;
    icon?: string;
};

interface PortalDropDownProps {
    options: IOption[];
    onSelect: (option: IOption) => void;
}

const PortalDropDown = ({ options, onSelect }: PortalDropDownProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<any>(null);
    const menuRef = useRef<any>(null);
    const toggleRef = useRef<HTMLDivElement | null>(null);
    const optionRefs = useRef<Array<HTMLDivElement | null>>([]);

    const toggleDropdown = () => {
        setIsOpen(!isOpen);
    };

    const handleOptionClick = (option: IOption) => {
        onSelect(option);
        toggleDropdown();
    };

    const handleClickOutside = (e: Event) => {
        if (
            dropdownRef.current &&
            !dropdownRef.current.contains(e.target) &&
            menuRef.current &&
            !menuRef.current.contains(e.target)
        ) {
            setIsOpen(false);
        }
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        requestAnimationFrame(() => {
            optionRefs.current[0]?.focus();
        });
    }, [isOpen]);

    return (
        <div ref={dropdownRef} className='dropdown'>
            <div
                ref={toggleRef}
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleDropdown();
                    }
                }}
                onClick={toggleDropdown}
                className='dropdown-toggleItem'
            >
                <div
                    className={`${styles['icon-cover']} ${isOpen ? styles['user-menu-active'] : ''}`}
                    data-testid='add-location-btn'
                >
                    <NexusIcon data-testid='add-icon' src={UserIcon} className={styles.icon}></NexusIcon>
                </div>
            </div>
            {isOpen &&
                createPortal(
                    <div
                        className='dropdown-menu user-dd-menu'
                        style={{
                            top: dropdownRef.current.getBoundingClientRect().top + 50,
                            left: dropdownRef.current.getBoundingClientRect().left - 200,
                        }}
                        ref={menuRef}
                    >
                        {options.map((option, inx) => (
                            <div
                                key={option.value}
                                ref={(element) => {
                                    optionRefs.current[inx] = element;
                                }}
                                className='dropdown-item user-dd-item'
                                tabIndex={0}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        handleOptionClick(option);
                                        return;
                                    }

                                    if (e.key === 'Escape') {
                                        e.preventDefault();
                                        setIsOpen(false);
                                        toggleRef.current?.focus();
                                        return;
                                    }

                                    if (e.key === 'Tab') {
                                        const lastIndex = options.length - 1;

                                        if (!e.shiftKey && inx === lastIndex) {
                                            e.preventDefault();
                                            optionRefs.current[0]?.focus();
                                        }

                                        if (e.shiftKey && inx === 0) {
                                            e.preventDefault();
                                            optionRefs.current[lastIndex]?.focus();
                                        }
                                    }
                                }}
                                onClick={() => handleOptionClick(option)}
                            >
                                <NexusIcon src={option.icon} /> {option.label}
                            </div>
                        ))}
                    </div>,
                    document.body,
                )}
        </div>
    );
};

export default PortalDropDown;
